import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from 'src/components/atoms/button/button';
import { Heading } from 'src/components/atoms/heading/heading';
import { Select, TOptionType } from 'src/components/atoms/select/select';
import Spinner from 'src/components/atoms/spinner/spinner';
import TimesheetTableBody from 'src/components/atoms/timesheet-table/timesheet-table-body';
import TimesheetTableHeader from 'src/components/atoms/timesheet-table/timesheet-table-header';
import { STARTING_YEAR } from 'src/config/constants';
import { useAppDispatch, useAppSelector } from 'src/reducers';
import { resetSearchUserCommission, searchUserCommissions } from 'src/reducers/search-commissions.reducer';
import { resetSearchUserTimesheet, searchUserTimesheets } from 'src/reducers/search-user-timesheets.reducer';
import { downloadUserTimesheet } from 'src/reducers/user-timesheet-download.reducer';
import { searchUserById } from 'src/reducers/user.reducer';
import { monthsOptions } from 'src/utils/options-utils';
import xlsDownload from '../../../shared/xls-download.png';
import { DOWNLOAD_TIMESHEET_PERMISSION, isAuthorizedToHandleCapability, VIEW_TIMESHEETS_PERMISSION } from '../../../utils/capability-utils';
import { setParamsToUrl } from '../../../utils/pagination-utils';
import Flex from '../../atoms/flex/flex';
import styles from './timesheets.module.scss';
import { isSystem } from 'src/utils/auth-utils';

const UserTimesheets = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const paramsFromUrl = new URLSearchParams(location.search);
  const yearFromUrl = paramsFromUrl.get('year');
  const monthFromUrl = paramsFromUrl.get('month');

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month();

  const [month, setMonth] = useState<number>(monthFromUrl ? Number(monthFromUrl) : currentMonth);
  const [year, setYear] = useState<number>(yearFromUrl ? Number(yearFromUrl) : currentYear);
  const { loading, failure, userTimesheets } = useAppSelector(state => state.searchUserTimesheets);

  const { commissions, loading: loadingCommissions, failure: failureCommissions } = useAppSelector(state => state.searchCommissions);

  const { loading: loadingDownload } = useAppSelector(state => state.downloadUserTimesheets);
  const { user } = useAppSelector(state => state.user);
  const { user: authenticatedUser } = useAppSelector(state => state.authentication);

  const days = dayjs().month(month).daysInMonth();

  const yearsOptions = () => {
    const startingYear = STARTING_YEAR;
    const years: TOptionType[] = [];
    for (let year = currentYear; year >= startingYear; year--) {
      years.push({
        label: `${year}`,
        value: year,
      });
    }
    return years;
  };

  const selectMonth = (data: TOptionType) => {
    const newMonth = data.value as unknown as number;
    setMonth(newMonth);
  };

  const selectYear = (data: TOptionType) => {
    const newYear = data.value as unknown as number;
    setYear(newYear);
  };

  const exportTimesheet = () => {
    dispatch(
      downloadUserTimesheet({
        userId: Number(params.id),
        period: `${year}-${month + 1}`,
      })
    );
  };

  useEffect(() => {
    if (params.id) {
      setParamsToUrl({ year, month }, navigate);
      dispatch(searchUserById({ id: Number(params.id) }));
      dispatch(
        searchUserTimesheets({
          userId: Number(params.id),
          period: `${year}-${month + 1}`,
        })
      );
    }
  }, [dispatch, month, year]);

  useEffect(() => {
    if (params?.id && user) {
      dispatch(
        searchUserCommissions({
          userId: Number(params.id),
          roleId: user.authority.id,
        })
      );
    }
  }, [dispatch, params.id]);

  useEffect(() => {
    return () => {
      dispatch(resetSearchUserTimesheet());
      dispatch(resetSearchUserCommission());
    };
  }, []);

  return (
    <div className="container xxl m-auto p-1">
      <Flex container alignItems="center" gap={10} className="mt-1 mb-1">
        <Button
          size="sm"
          color="white"
          rounded="rounded-sm"
          mode="outline"
          onClick={() => {
            user && isAuthorizedToHandleCapability(user, VIEW_TIMESHEETS_PERMISSION) ? navigate('/timesheets') : navigate('/');
          }}
        >
          Indietro
        </Button>
        <Flex container flexDirection="column">
          <Heading size={2} className={styles.title} content="Dettagli rapportini" />
          <div className={styles.subtitle}>{user?.anagraphic ? user?.anagraphic?.firstname + ' ' + user?.anagraphic?.lastname : user?.email}</div>
        </Flex>

        <Flex container alignItems="center" gap={10} className="ml-auto">
          <Select
            options={monthsOptions}
            onSelect={selectMonth}
            selected={month}
            size="md"
            rounded="rounded-md"
            mode="outline"
            color="light"
            showReset={false}
          />
          <Select
            options={yearsOptions()}
            onSelect={selectYear}
            selected={year}
            size="md"
            rounded="rounded-md"
            mode="outline"
            color="light"
            showReset={false}
          />
        </Flex>
      </Flex>

      <div className={styles.tableWrapper}>
        {(loading || loadingCommissions) && <Spinner />}
        {(!loading && failure) || (!loadingCommissions && failureCommissions && <>Errore durante il caricamento del rapportino</>)}
        {!loading && userTimesheets && !loadingCommissions && commissions && (
          <>
            <div className={styles.tableContainer}>
              <table cellSpacing={0}>
                <thead>
                  <TimesheetTableHeader year={year} month={month} days={days} />
                </thead>
                <TimesheetTableBody month={month} days={days} year={year} commissions={commissions} />
              </table>
            </div>
            <div className={styles.tableFooter}>
              <div className={styles.totals}>
                <div>
                  Totale ore del mese: {userTimesheets.statistics.total - userTimesheets?.statistics?.totalCommission['Smartworking'] || 0}
                </div>
                <div>Totale ore smartworking: {userTimesheets?.statistics?.totalCommission['Smartworking'] || 0}</div>
              </div>
              {authenticatedUser &&
                (isSystem(authenticatedUser.authority.name as string) ||
                  isAuthorizedToHandleCapability(authenticatedUser, DOWNLOAD_TIMESHEET_PERMISSION)) && (
                  <div className={styles.downloadXlsWrapper}>
                    <Button
                      tabIndex={0}
                      onClick={exportTimesheet}
                      size="md"
                      color="white"
                      mode="solid"
                      rounded="rounded-xl"
                      disabled={loadingDownload}
                    >
                      {loadingDownload ? (
                        <Spinner />
                      ) : (
                        <>
                          Scarica rapportino
                          <img src={xlsDownload} alt="xls download" className={styles.downloadXls} />
                        </>
                      )}
                    </Button>
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserTimesheets;
